import { Copy, FolderPlus, RefreshCcw, Upload } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { Button } from '@/app/components/ui/button';
import { useStore } from '@/app/providers/store';
import { cn } from '@/app/utils';
import type { MediaViewMode } from '../../../types';
import { MediaViewModeToggle } from './MediaViewModeToggle';

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface BreadcrumbLeadingProps {
  breadcrumbItems: BreadcrumbItem[];
  onNavigate: (path: string) => void;
  onCopyPath: () => void;
}

const BreadcrumbLeading = ({ breadcrumbItems, onNavigate, onCopyPath }: BreadcrumbLeadingProps) => (
  <div className="flex flex-wrap items-center gap-0.5 font-medium text-base text-system-white">
    <div className="flex flex-wrap items-center gap-0.5">
      <span className="text-system-white40">/</span>
      {breadcrumbItems.map((item, index) => (
        <button
          key={item.path}
          type="button"
          className="rounded-6 py-0.5 transition hover:bg-system-white08 hover:text-system-white"
          onClick={() => onNavigate(item.path)}
        >
          {index > 0 ? <span className="pr-0.5 text-system-white40">/</span> : null}
          {item.label}
        </button>
      ))}
    </div>
    <Button
      variant="text"
      size="sm"
      className="ml-0.5 h-6 shrink-0 rounded-8 px-1 text-system-white40 hover:bg-system-white08 hover:text-system-white"
      onClick={onCopyPath}
    >
      <Copy className="size-4" />
    </Button>
  </div>
);

interface MediaBrowserHeaderProps {
  leading: ReactNode;
  uploadingFile: boolean;
  loading: boolean;
  viewMode: MediaViewMode;
  onViewModeChange: (mode: MediaViewMode) => void;
  onOpenCreateFolder: () => void;
  onUploadClick: () => void;
  onRefresh: () => void;
}

const MediaBrowserHeaderBase = ({
  leading,
  uploadingFile,
  loading,
  viewMode,
  onViewModeChange,
  onOpenCreateFolder,
  onUploadClick,
  onRefresh,
}: MediaBrowserHeaderProps) => {
  const t = useTranslations();
  const { clawStatus } = useStore((state) => ({
    clawStatus: state.clawStatus,
  }));

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">{leading}</div>
      <div className="flex items-center gap-1">
        <MediaViewModeToggle value={viewMode} onChange={onViewModeChange} />
        {clawStatus === 'CLAW_STATUS_ACTIVE' && (
          <Button
            variant="text"
            size="sm"
            className="rounded-10 px-2 text-system-white40 hover:bg-system-white08 hover:text-system-white"
            title={t('claw.file.newFolder')}
            aria-label={t('claw.file.newFolder')}
            onClick={onOpenCreateFolder}
          >
            <FolderPlus className="size-4" />
          </Button>
        )}
        {clawStatus === 'CLAW_STATUS_ACTIVE' && (
          <Button
            variant="text"
            size="sm"
            loading={uploadingFile}
            className="rounded-10 px-2 text-system-white40 hover:bg-system-white08 hover:text-system-white"
            title={t('claw.file.upload')}
            aria-label={t('claw.file.upload')}
            onClick={onUploadClick}
          >
            {!uploadingFile && <Upload className="size-4" />}
          </Button>
        )}
        <Button
          variant="text"
          size="sm"
          className="rounded-10 px-2 text-system-white40 hover:bg-system-white08 hover:text-system-white"
          title={t('claw.file.table.refresh')}
          aria-label={t('claw.file.table.refresh')}
          onClick={onRefresh}
        >
          <RefreshCcw className={cn('size-4', loading ? 'animate-spin' : '')} />
        </Button>
      </div>
    </div>
  );
};

interface MediaBrowserBreadcrumbHeaderProps extends Omit<MediaBrowserHeaderProps, 'leading'>, BreadcrumbLeadingProps {}

export const MediaBrowserBreadcrumbHeader = ({
  breadcrumbItems,
  onNavigate,
  onCopyPath,
  ...rest
}: MediaBrowserBreadcrumbHeaderProps) => (
  <MediaBrowserHeaderBase
    {...rest}
    leading={<BreadcrumbLeading breadcrumbItems={breadcrumbItems} onNavigate={onNavigate} onCopyPath={onCopyPath} />}
  />
);

interface MediaBrowserTitleHeaderProps extends Omit<MediaBrowserHeaderProps, 'leading'> {
  title: string;
}

export const MediaBrowserTitleHeader = ({ title, ...rest }: MediaBrowserTitleHeaderProps) => (
  <MediaBrowserHeaderBase
    {...rest}
    leading={<div className="min-w-0 truncate font-medium text-base text-system-white">{title}</div>}
  />
);
